import React from 'react';
import { motion } from 'framer-motion';
import {
    DevicePhoneMobileIcon,
    CloudIcon,
    CalendarDaysIcon,
    ChartBarIcon,
    SignalIcon,
    ChatBubbleBottomCenterTextIcon,
    ShieldCheckIcon,
    BeakerIcon,
    BugAntIcon,
    CameraIcon,
    SunIcon,
    MapIcon
} from '@heroicons/react/24/outline';
import SEO from '../../components/SEO';

const OurApp = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.15 }
        }
    };

    // Features data from user slides (Text & Icons only)
    const features = [
        {
            title: "Soil Health Monitoring",
            desc: "Track key soil parameters: Nitrogen (N), Phosphorus (P), Potassium (K), Organic Carbon, pH.",
            icon: BeakerIcon
        },
        {
            title: "Pest & Disease Forewarning",
            desc: "AI-powered alerts for pest and disease risks. Preventive guidance to protect crops in advance.",
            icon: BugAntIcon
        },
        {
            title: "Image-Based Advisory",
            desc: "Upload crop photos for instant expert analysis. Detect nutrient deficiencies, pest attacks, or disease symptoms.",
            icon: CameraIcon
        },
        {
            title: "Smart Crop Calendar",
            desc: "Season-wise schedule for sowing, irrigation, fertilization, and harvesting. Ensures timely operations for maximum yield.",
            icon: CalendarDaysIcon
        },
        {
            title: "Irrigation Management",
            desc: "Scientific advisory on when and how much to irrigate. Saves water and reduces costs.",
            icon: CloudIcon
        },
        {
            title: "Soil Moisture Tracking",
            desc: "Real-time soil moisture data. Maintain the right balance for healthy crop growth.",
            icon: ChartBarIcon
        },
        {
            title: "Weather Forecast",
            desc: "Daily & weekly forecasts tailored for agriculture. Plan irrigation, spraying, and harvesting with confidence.",
            icon: SunIcon
        },
        {
            title: "Crop Health Monitoring",
            desc: "Satellite & sensor-based health tracking. Early detection of stress, nutrient deficiency, or pest issues.",
            icon: SignalIcon
        },
        {
            title: "LSWI (Land Surface Water Index)",
            desc: "Monitors water stress & surface moisture. Enables smart irrigation decisions and efficient water use.",
            icon: MapIcon
        }
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            <SEO
                title="Download Farmget App - Best Agriculture App for Farmers"
                description="Download Farmget App for real-time crop advisory, weather updates, soil health monitoring, and personalized farming tips."
                keywords="Farmget App, Agriculture App, Crop Doctor, Soil Testing App, Kisan App Download, Smart Farming App"
            />
            {/* Hero Section - Preserved */}
            <section className="relative h-[80vh] flex items-center bg-gray-900 text-white overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src="/assets/app_hero_premium.jpg"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1740&q=80' }}
                        alt="Digital Farming"
                        className="w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-transparent z-10"></div>
                </div>

                <div className="relative z-20 container mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={staggerContainer}
                    >
                        <motion.h1 variants={fadeInUp} className="text-5xl md:text-7xl font-bold leading-tight mb-6">
                            Smart <span className="text-green-400">Digital Platform</span>
                        </motion.h1>
                        <motion.p variants={fadeInUp} className="text-xl text-gray-300 mb-8 font-light max-w-lg">
                            An integrated ecosystem connecting farmers to expert advisory, market insights, and precision analytics.
                        </motion.p>
                        <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                            {/* Google Play Button */}
                            <a href="https://play.google.com/store/apps/details?id=com.marvelous.farmget" target="_blank" rel="noopener noreferrer" className="flex items-center bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-green-500/40 w-full sm:w-auto min-w-[200px]">
                                <svg className="w-8 h-8 mr-3 text-white" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M3.609 1.814L13.792 12 3.61 22.186a2.1 2.1 0 011.693-.191l-.224-.316-.279.167.332-.236a1.642 1.642 0 01-.151-1.2l-1.372-18.596zM15.353 13.56l2.975 2.976-5.26 3.036L15.353 13.56zm1.144-1.144L21.49 9.389a1.638 1.638 0 01.916 1.488 1.638 1.638 0 01-.916 1.488l-4.993 2.882-1.144-1.144zM15.353 10.44l-2.285-2.286 5.26-3.036-2.975 2.975.385.347z" />
                                </svg>
                                <div className="text-left">
                                    <div className="text-[10px] leading-none uppercase text-green-100 mb-0.5">Get it on</div>
                                    <div className="text-base font-bold leading-none">Google Play</div>
                                </div>
                            </a>

                            {/* App Store Button */}
                            <a href="https://apps.apple.com/in/app/farmget/id6752594841" target="_blank" rel="noopener noreferrer" className="flex items-center bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-md text-white px-6 py-3 rounded-xl font-bold transition-all w-full sm:w-auto min-w-[200px]">
                                <svg className="w-8 h-8 mr-3 text-white" viewBox="0 0 384 512" fill="currentColor">
                                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 46.9 126.7 84.5 126.7 33 0 47.3-24.3 88.4-24.3 39.9 0 51.6 23 90 23 37.2 0 62.6-69.3 87.2-132.8-51.5-17.7-86.8-59-89.8-108.6zM229.4 100.3c15.8-19.9 26.2-43.9 23.2-69.6-21.8 1.4-53.7 15.3-72 37.6-13.7 16.5-24 40.7-22.1 66.8 24.3 1.9 50.7-13.8 70.9-34.8z" />
                                </svg>
                                <div className="text-left">
                                    <div className="text-[10px] leading-none uppercase text-gray-300 mb-0.5">Download on the</div>
                                    <div className="text-base font-bold leading-none">App Store</div>
                                </div>
                            </a>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-24 px-6 bg-white">
                <div className="container mx-auto max-w-7xl">
                    <div className="text-center mb-16">
                        <span className="text-green-600 font-bold tracking-widest uppercase text-sm">Farm Advisory System</span>
                        <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mt-2">Key Features of App</h2>
                        <div className="w-24 h-1 bg-[#9FE870] mx-auto rounded-full mt-6"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, idx) => (
                            <motion.div
                                key={idx}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeInUp}
                                className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-green-200 transition-all group"
                            >
                                <div className="bg-green-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <feature.icon className="w-8 h-8 text-green-600" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default OurApp;
